// src/runtime/ContentStrategyRuntime/service.ts
// ============================================
// ContentStrategyService - 策略简报生成编排
//
// 依据白皮书第十四章《Content Protocol》：
//   ① 品牌选择 → ② 平台选择 → ③ 内容目标 → ④ 知识检索 → ⑤ 策略简报 → ⑥ 素材生成 → ⑦ 发布
//
// 职责：
//   - generate_brief：生成 Platform Brief（策略简报）
//       1. 读取该平台 Platform Spec（99_Core/Content_Protocol/Platform_{name}.md）
//       2. 强制调用 KnowledgeRuntime 检索知识库（Rule 04 可追溯）
//       3. 组合品牌身份 + 平台规格 + 内容目标 → 输出简报
//   - 简报落盘：XEAOS/Daily/Brief-{date}-{platform}.md
// ============================================

import * as fs from 'fs';
import * as path from 'path';
import { RuntimeName } from '../../types/runtime';
import type { RuntimeManager } from '../RuntimeManager';
import { vaultConfig } from '../shared/vaultConfig';
import { BrowserVaultReader } from '../shared/BrowserVaultReader';
import { DailyExperienceService, dailyExperienceService } from './experienceService';
import config, {
  BRANDS,
  PLATFORMS,
  GOAL_HOOK_STRATEGY,
  GOAL_CTA_STRATEGY,
} from './config';
import {
  ContentStrategyQuery,
  ContentStrategyCommand,
  CONTENT_GOAL_LABEL,
  type BrandIdentity,
  type PlatformBrief,
  type ContentGoal,
  type ContentStrategyResult,
} from './types';

/** 平台规格（从规格书解析） */
interface PlatformSpecData {
  audience: string;
  tone: string;
  goal: string;
  structure: string;
  taboos: string[];
  duration: string;
  /** v5.2.4 规格书「## 生产约束」段落的规则列表（通俗化/排版/插图/篇幅等，强制进生成约束） */
  productionRules?: string[];
}

export class ContentStrategyService {
  private logger: Console;
  private runtimeManager: RuntimeManager | null;
  private reader = BrowserVaultReader.getInstance();
  private initialized = false;

  constructor(runtimeManager: RuntimeManager | null = null, logger: Console = console) {
    this.runtimeManager = runtimeManager;
    this.logger = logger;
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    this.logger.info('[ContentStrategyService] ✅ 初始化完成');
  }

  // ============================================
  // 查询接口
  // ============================================

  async query<T>(
    type: ContentStrategyQuery,
    params: Record<string, any> = {}
  ): Promise<ContentStrategyResult<T>> {
    if (!this.initialized) await this.initialize();
    try {
      switch (type) {
        case ContentStrategyQuery.ListBrands:
          return this.ok(BRANDS as T);
        case ContentStrategyQuery.GetBrand: {
          const brand = BRANDS.find((b) => b.id === params.id);
          return brand ? this.ok(brand as T) : this.fail(`品牌不存在: ${params.id}`);
        }
        case ContentStrategyQuery.ListPlatforms:
          return this.ok(PLATFORMS as T);
        case ContentStrategyQuery.GetPlatformSpec: {
          const spec = await this.loadPlatformSpec(params.platform || params.id);
          return spec
            ? this.ok(spec as T)
            : this.fail(`平台规格书不存在: ${params.platform || params.id}`);
        }
        case ContentStrategyQuery.GetDailyExperience: {
          const experiences = await dailyExperienceService.extractToday(3);
          return this.ok(experiences as T);
        }
        default:
          return this.fail(`未知的查询类型: ${type}`);
      }
    } catch (err: any) {
      return this.fail(err?.message || String(err));
    }
  }

  // ============================================
  // 命令接口
  // ============================================

  async execute<T>(
    command: ContentStrategyCommand,
    params: Record<string, any> = {}
  ): Promise<ContentStrategyResult<T>> {
    if (!this.initialized) await this.initialize();
    try {
      switch (command) {
        case ContentStrategyCommand.GenerateBrief: {
          const brief = await this.generateBrief(params);
          return brief ? this.ok(brief as T) : this.fail('生成简报失败');
        }
        case ContentStrategyCommand.GenerateBriefAndSave: {
          const brief = await this.generateBrief(params);
          if (!brief) return this.fail('生成简报失败');
          const saved = await this.saveBrief(brief);
          return this.ok({ brief, saved } as T);
        }
        case ContentStrategyCommand.PersistDailyExperience: {
          const experiences = await dailyExperienceService.extractToday(3);
          const daily = await dailyExperienceService.persistDaily(experiences);
          return this.ok(daily as T);
        }
        default:
          return this.fail(`未知的命令: ${command}`);
      }
    } catch (err: any) {
      return this.fail(err?.message || String(err));
    }
  }

  // ============================================
  // 核心：生成策略简报
  // ============================================

  private async generateBrief(params: Record<string, any>): Promise<PlatformBrief | null> {
    // ① 品牌选择
    const brandId = String(params.brand || params.brandId || 'xeaos');
    const brand = BRANDS.find((b) => b.id === brandId) || BRANDS[BRANDS.length - 1];

    // ② 平台选择
    const platformId = String(params.platform || params.platformId || 'wechat');
    const platformMeta = PLATFORMS.find((p) => p.id === platformId);
    if (!platformMeta) return null;

    // ③ 内容目标
    const goal = (params.goal || 'lead_gen') as ContentGoal;

    // ③.5 经验驱动选题：无显式选题时，取今日真实经验作为素材
    // 白皮书原则：「每天沉淀一条经验，而不是每天硬找一个选题」
    let experience: { topic: string; material: string; source: string } | undefined;
    if (!params.topic) {
      try {
        const material = await dailyExperienceService.asTopicMaterial();
        if (material) {
          experience = {
            topic: material.topic,
            material: material.material,
            source: 'DailyExperienceService',
          };
        }
      } catch (err) {
        this.logger.debug('[ContentStrategy] 经验素材不可用: ' + String(err));
      }
    }

    // ④ 知识检索（必须）
    const knowledgeRefs = await this.retrieveKnowledge(
      brand,
      platformMeta.name,
      goal,
      params.topic || experience?.topic
    );

    // ⑤ 读取该平台 Platform Spec
    const spec = await this.loadPlatformSpec(platformId);

    // 组合简报
    const brief: PlatformBrief = {
      brand,
      platform: platformId,
      platformName: platformMeta.name,
      goal,
      goalLabel: CONTENT_GOAL_LABEL[goal],
      persona: this.buildPersona(brand, platformId),
      audience: spec?.audience || brand.targetCustomer,
      structure: spec?.structure || '观点 → 案例 → 方法 → 总结',
      taboos: spec?.taboos || ['无观点', '广告味浓'],
      hook: GOAL_HOOK_STRATEGY[goal] || GOAL_HOOK_STRATEGY.lead_gen,
      cta: GOAL_CTA_STRATEGY[goal] || GOAL_CTA_STRATEGY.lead_gen,
      tone: spec?.tone || platformMeta.defaultTone,
      duration: spec?.duration || platformMeta.defaultDuration,
      knowledgeRefs,
      constraints: this.buildConstraints(brand, platformMeta.name, goal, spec),
      generatedAt: new Date().toISOString(),
      experience,
    };
    return brief;
  }

  // ============================================
  // 知识检索（Rule 04：强制 + 可追溯）
  // ============================================

  private async retrieveKnowledge(
    brand: BrandIdentity,
    platformName: string,
    goal: ContentGoal,
    topic?: string
  ): Promise<Array<{ title: string; path?: string }>> {
    const refs: Array<{ title: string; path?: string }> = [];
    if (!this.runtimeManager) return refs;
    try {
      const query = topic || `${brand.name} ${brand.valueProp}`;
      const resp = await this.runtimeManager.query(RuntimeName.Knowledge, {
        type: 'search_cards',
        query,
        limit: 3,
      } as any);
      const data: any = resp?.success ? resp.data : resp;
      const cards = Array.isArray(data) ? data : data?.cards ?? [];
      for (const c of cards.slice(0, 3)) {
        refs.push({
          title: String(c?.title || c?.fileName || '知识卡片'),
          path: String(c?.filePath || c?.id || ''),
        });
      }
    } catch (err) {
      this.logger.debug('[ContentStrategy] 知识检索不可用: ' + String(err));
    }
    return refs;
  }

  // ============================================
  // Platform Spec 读取（优先 Vault 规格书，兜底默认值）
  // ============================================

  private async loadPlatformSpec(platformId: string): Promise<PlatformSpecData | null> {
    const meta = PLATFORMS.find((p) => p.id === platformId);
    if (!meta) return null;
    try {
      const relPath = `${config.platformSpecDir}/${meta.specFile}`;
      let raw: string | null = null;
      if (vaultConfig.isBrowser) {
        raw = await this.reader.readFile(relPath);
      } else {
        const absPath = path.join(vaultConfig.vaultRoot, relPath);
        if (fs.existsSync(absPath)) {
          raw = fs.readFileSync(absPath, 'utf-8');
        }
      }
      if (!raw) return null;
      return this.parsePlatformSpec(raw, meta.defaultTone, meta.defaultDuration);
    } catch (err) {
      this.logger.debug('[ContentStrategy] 读取 Platform Spec 失败: ' + String(err));
      return null;
    }
  }

  /** 从规格书 Markdown 中解析关键字段 */
  private parsePlatformSpec(
    raw: string,
    defaultTone: string,
    defaultDuration: string
  ): PlatformSpecData {
    const get = (label: string): string => {
      const re = new RegExp(`\\|\\s*${label}[^|]*\\|\\s*([^|]+)\\s*\\|`);
      const m = raw.match(re);
      return m ? m[1].trim() : '';
    };
    const audience = get('Audience') || get('受众');
    const tone = get('Tone') || get('语气') || defaultTone;
    const goal = get('Goal') || get('目标');
    const structure = get('Structure') || get('结构');
    const taboosRaw = get('Taboos') || get('禁忌');
    const duration = get('Duration/WordLimit') || get('时长/字数') || defaultDuration;
    const taboos = taboosRaw
      ? taboosRaw.split(/、|，|,/).map((s) => s.trim()).filter(Boolean)
      : [];
    // v5.2.4 解析「## 生产约束」段落：提取所有 "- xxx" 列表项作为生产规则
    const productionRules: string[] = [];
    const prodMatch = raw.match(/##\s*生产约束\s*\n([\s\S]*?)(?=##|$)/);
    if (prodMatch) {
      const ruleRe = /^\s*[-*]\s+(.+)$/gm;
      let rm: RegExpExecArray | null;
      while ((rm = ruleRe.exec(prodMatch[1])) !== null) {
        const rule = rm[1].trim().replace(/^(\*\*[^*]+\*\*)/, '$1'); // 去粗体标记保留文本
        if (rule) productionRules.push(rule);
      }
    }
    return { audience, tone, goal, structure, taboos, duration, productionRules };
  }

  // ============================================
  // 组合辅助
  // ============================================

  /** 平台人设：品牌身份 × 平台定位 */
  private buildPersona(brand: BrandIdentity, platformId: string): string {
    const base = `${brand.name} 在${this.platformName(platformId)}上的账号`;
    const personaMap: Record<string, string> = {
      douyin_enterprise: `${base}：专业顾问，为${brand.targetCustomer}输出行业干货`,
      douyin_personal: `${base}：真实创业者，分享${brand.product}背后的故事与思考`,
      xiaohongshu: `${base}：同行朋友，分享${brand.product}的使用经验与避坑`,
      wechat: `${base}：行业观点输出者，围绕${brand.valueProp}建立认知`,
      shipinhao: `${base}：可信的行业导师，稳健输出观点与方法`,
      zhihu: `${base}：领域权威，专业克制地回答行业问题`,
      bilibili: `${base}：真诚的深度分享者，输出扎实干货`,
    };
    return personaMap[platformId] || base;
  }

  /** 生成提示词约束（供 Platform Adapter 翻译成生成提示词） */
  private buildConstraints(
    brand: BrandIdentity,
    platformName: string,
    goal: ContentGoal,
    spec: PlatformSpecData | null
  ): string[] {
    const c: string[] = [];
    c.push(`品牌：${brand.name}（${brand.tagline}）`);
    c.push(`核心产品：${brand.product}`);
    c.push(`目标客户：${brand.targetCustomer}`);
    c.push(`价值主张：${brand.valueProp}`);
    c.push(`平台：${platformName}`);
    c.push(`内容目标：${CONTENT_GOAL_LABEL[goal]}`);
    c.push(`语气：${spec?.tone || '专业'}`);
    c.push(`结构：${spec?.structure || '观点 → 案例 → 方法 → 总结'}`);
    if (spec?.taboos && spec.taboos.length > 0) {
      c.push(`禁忌：${spec.taboos.join('、')}`);
    }
    c.push(`时长/字数：${spec?.duration || '不限'}`);
    c.push(`GEO/SEO 关键词植入：${brand.keywords.slice(0, 5).join('、')}`);
    // v5.2.4 规格书「生产约束」规则（通俗化/段落排版/插图位/篇幅等）强制进生成约束
    if (spec?.productionRules && spec.productionRules.length > 0) {
      c.push(`生产约束：${spec.productionRules.join('；')}`);
    }
    return c;
  }

  private platformName(id: string): string {
    return PLATFORMS.find((p) => p.id === id)?.name || id;
  }

  // ============================================
  // 简报落盘
  // ============================================

  private async saveBrief(brief: PlatformBrief) {
    const date = new Date().toISOString().slice(0, 10);
    const content = this.serializeBrief(brief, date);
    const relPath = `${config.briefDir}/Brief-${date}-${brief.platform}.md`;
    if (vaultConfig.isBrowser) {
      const ok = await this.reader.writeFile(relPath, content);
      return { success: ok, filePath: relPath };
    }
    try {
      const absPath = path.join(vaultConfig.vaultRoot, relPath);
      fs.mkdirSync(path.dirname(absPath), { recursive: true });
      fs.writeFileSync(absPath, content, 'utf-8');
      return { success: true, filePath: relPath };
    } catch (err: any) {
      return { success: false, error: err?.message || String(err) };
    }
  }

  private serializeBrief(brief: PlatformBrief, date: string): string {
    const L: string[] = [];
    L.push('---');
    L.push('type: platform_brief');
    L.push(`date: ${date}`);
    L.push(`brand: ${brief.brand.id}`);
    L.push(`platform: ${brief.platform}`);
    L.push(`goal: ${brief.goal}`);
    L.push('---');
    L.push('');
    L.push(`# 策略简报：${brief.brand.name} × ${brief.platformName}（${brief.goalLabel}）`);
    L.push('');
    L.push(`> 由 Content Strategy Runtime 生成（${brief.generatedAt}）`);
    L.push('');
    L.push('## 品牌', '');
    L.push(`- 品牌：${brief.brand.name}`);
    L.push(`- 定位：${brief.brand.tagline}`);
    L.push(`- 产品：${brief.brand.product}`);
    L.push(`- 目标客户：${brief.brand.targetCustomer}`);
    L.push(`- 价值主张：${brief.brand.valueProp}`);
    L.push('');
    L.push('## 平台人设', '', brief.persona, '');
    L.push('## 受众画像', '', brief.audience, '');
    L.push('## 内容结构', '', brief.structure, '');
    L.push('## 禁忌', '');
    brief.taboos.forEach((t) => L.push(`- ${t}`));
    L.push('');
    L.push('## 钩子策略', '', brief.hook, '');
    L.push('## CTA 策略', '', brief.cta, '');
    L.push('## 语气 / 时长', '', `- 语气：${brief.tone}`, `- 时长/字数：${brief.duration}`, '');
    L.push('## 知识检索引用（Rule 04）', '');
    if (brief.knowledgeRefs.length > 0) {
      brief.knowledgeRefs.forEach((r) => L.push(`- ${r.title}${r.path ? `（${r.path}）` : ''}`));
    } else {
      L.push('- （暂无可用知识，生产主流程仍需走知识检索步骤）');
    }
    L.push('');
    L.push('## 生成提示词约束', '');
    brief.constraints.forEach((c) => L.push(`- ${c}`));
    L.push('');
    return L.join('\n');
  }

  private ok<T>(data: T): ContentStrategyResult<T> {
    return { success: true, data, timestamp: new Date().toISOString() };
  }

  private fail<T>(error: string): ContentStrategyResult<T> {
    return { success: false, error, timestamp: new Date().toISOString() };
  }
}

export default ContentStrategyService;
